import { useQuery } from '@tanstack/react-query';
import { AnimatePresence } from 'framer-motion';
import { useState } from 'react';
import styled from 'styled-components';

import Button from '@/components/Button';
import {
  HeadingsRow,
  PageHeading,
  SrOnly,
  TeamHeading,
} from '@/components/Layout/Parts';
import SearchBar from '@/components/SearchBar';
import { Table, TableContent, Th } from '@/components/Table';
import useDebounce from '@/hooks/useDebounce';
import { EmployeeResource } from '@/types/resources';
import { api } from '@/utils/api';

import SettingsNavigation from '../components/SettingsNavigation';
import EmployeeForm from './EmployeeForm';
import EmployeeTableRow from './EmployeeTableRow';

const StyledTable = styled(Table)`
  box-shadow: 0px 0px 2px rgba(0, 0, 0, 0.08);
  th,
  td {
    &:first-child {
      padding-left: 24px;
    }
    &:last-child {
      padding-right: 24px;
      width: 64px;
    }
  }
  td {
    padding-top: 20px;
    padding-bottom: 20px;
  }
`;

const Employees = () => {
  const [search, setSearch] = useState('');
  const [showCreationForm, setShowCreationForm] = useState(false);

  const debouncedSearch = useDebounce(search, 500);

  const {
    data: employees,
    isLoading,
    isError,
  } = useQuery(
    [
      'employees',
      {
        ...(!!debouncedSearch && { debouncedSearch }),
      },
    ],
    () =>
      api.get<never, EmployeeResource[]>('/v1/employees', {
        params: {
          ...(!!debouncedSearch && { 'filter[search]': debouncedSearch }),
        },
      }),
    {
      staleTime: 120 * 1000,
    },
  );

  return (
    <>
      <HeadingsRow>
        <div>
          <TeamHeading />
          <PageHeading>Settings</PageHeading>
        </div>
        <Button onClick={() => setShowCreationForm(true)}>
          Create Employee
        </Button>
      </HeadingsRow>
      <SettingsNavigation />
      <AnimatePresence>
        {showCreationForm && (
          <EmployeeForm onClose={() => setShowCreationForm(false)} />
        )}
      </AnimatePresence>
      <SearchBar
        value={search}
        setValue={setSearch}
        placeholder="Search by name"
      />
      <StyledTable $fixed>
        <thead>
          <tr>
            <Th>Name</Th>
            <Th>Roles</Th>
            <Th $actionsCol>
              <SrOnly>Actions</SrOnly>
            </Th>
          </tr>
        </thead>
        <tbody>
          <TableContent
            colSpan={3}
            isLoading={isLoading}
            hasData={!!employees?.length}
            isError={isError}
          >
            {employees?.map((employee) => (
              <EmployeeTableRow key={employee.id} employee={employee} />
            ))}
          </TableContent>
        </tbody>
      </StyledTable>
    </>
  );
};

export default Employees;
